// Fictional "Zorya" city hospital, internal-medicine ward, 12-hour day shift.
// See README.md for the schema.
export default {
  id: 'nurse',
  occupationId: '66b280672a4e4285b87ebad7',
  occupationName: { en: 'Nurse', ua: 'Медсестра' },
  format: 'ward',
  title: { en: 'A shift as a ward nurse', ua: 'Зміна медсестри у відділенні' },
  tagline: {
    en: 'Eighteen patients, one doctor stuck in the ER and a medication chart that does not add up',
    ua: 'Вісімнадцять пацієнтів, один лікар, що застряг у приймальному, і лист призначень, який не сходиться'
  },
  minutes: 11,
  intro: {
    en: 'You are a staff nurse on the internal-medicine ward of Zorya City Hospital. Day shift, 08:00 to 20:00, 18 patients, and the only doctor also covers the emergency room today. Your partner nurse is Svitlana, the aide is Yurii.',
    ua: 'Ти медсестра терапевтичного відділення міської лікарні «Зоря». Денна зміна з 08:00 до 20:00, 18 пацієнтів, а єдиний лікар сьогодні ще й чергує у приймальному. Поруч медсестра Світлана й молодший медбрат Юрій.'
  },
  goal: {
    en: 'Get every patient to 20:00 safer than you found them, without losing the team',
    ua: 'Довести кожного пацієнта до 20:00 у більшій безпеці, ніж прийняла, і не втратити команду'
  },
  kpis: [
    { key: 'safety', label: { en: 'Patient safety', ua: 'Безпека пацієнтів' }, start: 60 },
    { key: 'flow', label: { en: 'Ward flow', ua: 'Ритм відділення' }, start: 55 },
    { key: 'team', label: { en: 'Team', ua: 'Команда' }, start: 65 }
  ],
  competencies: [
    {
      key: 'clinical',
      label: { en: 'Clinical judgement', ua: 'Клінічне мислення' },
      skill: { _id: '686282c1aa8e5a92e3bc1719', name: 'Клінічні протоколи' }
    },
    {
      key: 'accuracy',
      label: { en: 'Accuracy', ua: 'Точність' },
      skill: { _id: '686282c1aa8e5a92e3bc1408', name: 'Точність' }
    },
    {
      key: 'communication',
      label: { en: 'Communication', ua: 'Комунікація' },
      skill: { _id: '686282c1aa8e5a92e3bc133c', name: 'Комунікативність' }
    },
    {
      key: 'priorities',
      label: { en: 'Prioritisation', ua: 'Пріоритизація' },
      skill: { _id: '686282c1aa8e5a92e3bc140a', name: 'Управління часом і розстановка пріоритетів' }
    },
    {
      key: 'ethics',
      label: { en: 'Ethics and safety', ua: 'Етика й безпека' },
      skill: {
        _id: '686282c1aa8e5a92e3bc177c',
        name: 'Обізнаність з медичними регламентами та протоколами'
      }
    }
  ],
  people: [
    {
      key: 'doctor',
      name: { en: 'Dr. Kovalenko', ua: 'Лікар Коваленко' },
      role: { en: 'Attending physician', ua: 'Лікар-терапевт' },
      initials: 'DK',
      color: '#30b0c7'
    },
    {
      key: 'nurse',
      name: { en: 'Svitlana', ua: 'Світлана' },
      role: { en: 'Ward nurse', ua: 'Медсестра відділення' },
      initials: 'SV',
      color: '#9c27b0'
    },
    {
      key: 'head',
      name: { en: 'Nadiia', ua: 'Надія' },
      role: { en: 'Head nurse', ua: 'Старша медсестра' },
      initials: 'NA',
      color: '#19857b'
    },
    {
      key: 'aide',
      name: { en: 'Yurii', ua: 'Юрій' },
      role: { en: 'Nursing aide', ua: 'Молодший медбрат' },
      initials: 'YU',
      color: '#4f9a04'
    },
    {
      key: 'daughter',
      name: { en: 'Oksana', ua: 'Оксана' },
      role: { en: "Patient's daughter", ua: 'Донька пацієнта' },
      initials: 'OK',
      color: '#d5921b'
    },
    {
      key: 'patient',
      name: { en: 'Mr. Marchenko', ua: 'Пан Марченко' },
      role: { en: 'Patient, bed 12', ua: 'Пацієнт, ліжко 12' },
      initials: 'MA',
      color: '#e25510'
    }
  ],
  steps: [
    {
      type: 'triage',
      id: 's1',
      at: '08:10',
      comp: ['clinical', 'priorities'],
      traits: ['I', 'S'],
      text: {
        en: 'Handover is done and five patients need you at once. Put them in the order you go to them.',
        ua: 'Передача зміни закінчилась, і п’ятеро пацієнтів потребують тебе одночасно. Розстав їх у порядку, в якому підійдеш.'
      },
      items: [
        {
          id: 'chest',
          title: { en: 'Bed 4, Mr. Bondar, 67', ua: 'Ліжко 4, пан Бондар, 67 років' },
          detail: {
            en: 'Chest pain for 10 minutes, pale and sweating. BP 85/50, pulse 112, SpO2 93%.',
            ua: 'Біль у грудях уже 10 хвилин, блідий, спітнілий. АТ 85/50, пульс 112, SpO2 93%.'
          }
        },
        {
          id: 'sugar',
          title: { en: 'Bed 7, Mr. Tkachuk, 58', ua: 'Ліжко 7, пан Ткачук, 58 років' },
          detail: {
            en: 'Type 2 diabetes on insulin. Glucose 3.1 mmol/L, drowsy, hands trembling, skipped breakfast.',
            ua: 'Діабет 2 типу на інсуліні. Глюкоза 3,1 ммоль/л, сонливий, тремтять руки, не снідав.'
          }
        },
        {
          id: 'confused',
          title: { en: 'Bed 2, Mrs. Hnatiuk, 80', ua: 'Ліжко 2, пані Гнатюк, 80 років' },
          detail: {
            en: 'Confused since morning, which is new for her. Keeps trying to climb over the bed rail. BP 130/80, SpO2 94%.',
            ua: 'Зранку сплутана, раніше такого не було. Намагається перелізти через бортик ліжка. АТ 130/80, SpO2 94%.'
          }
        },
        {
          id: 'fever',
          title: { en: 'Bed 11, Mrs. Lysenko, 54', ua: 'Ліжко 11, пані Лисенко, 54 роки' },
          detail: {
            en: 'Pneumonia, second day of antibiotics. Temperature 38.2, pulse 96, asks for something for the fever.',
            ua: 'Пневмонія, другий день антибіотиків. Температура 38,2, пульс 96, просить щось від жару.'
          }
        },
        {
          id: 'water',
          title: { en: 'Bed 15, Mr. Savchuk, 45', ua: 'Ліжко 15, пан Савчук, 45 років' },
          detail: {
            en: 'Asks for a jug of water and a second pillow. Vitals normal at 07:30.',
            ua: 'Просить глек води й другу подушку. Показники о 07:30 у нормі.'
          }
        }
      ],
      correctOrder: ['chest', 'sugar', 'confused', 'fever', 'water'],
      explain: {
        en: 'Chest pain with a pressure of 85/50 can be a heart attack or shock, so the doctor is called from that bedside. Glucose 3.1 with drowsiness is fixed in two minutes with glucose, but drops fast if you wait. New confusion in an 80-year-old needs a cause, and a fall is one rail away. Fever 38.2 on day two of antibiotics is expected and stable. Water and a pillow are a job for Yurii.',
        ua: 'Біль у грудях при тиску 85/50 може бути інфарктом або шоком, тож лікаря кличеш від цього ліжка. Глюкоза 3,1 із сонливістю виправляється глюкозою за дві хвилини, але без тебе падає швидко. Нова сплутаність у 80 років потребує причини, а до падіння один бортик. Температура 38,2 на другий день антибіотиків очікувана й стабільна. Вода й подушка — робота для Юрія.'
      },
      fx: { ok: { safety: 6 }, fail: { safety: -6, energy: -4 } }
    },
    {
      type: 'chat',
      id: 's2',
      at: '08:40',
      comp: ['clinical', 'communication'],
      traits: ['C', 'S'],
      messages: [
        {
          from: 'doctor',
          text: {
            en: 'Stuck in the ER for another hour at least. Bed 9, Mrs. Shevchuk: potassium 2.9, I added potassium chloride 7.5% 20 ml IV to her chart. Please give it now.',
            ua: 'У приймальному ще щонайменше годину. Ліжко 9, пані Шевчук: калій 2,9, я дописав у лист калію хлорид 7,5% 20 мл в/в. Введи, будь ласка, зараз.'
          }
        },
        {
          from: 'nurse',
          text: {
            en: 'He wrote "IV" and nothing else. No diluent, no rate. Your call, I am off to change drips.',
            ua: 'Він написав «в/в» і все. Ні розчинника, ні швидкості. Вирішуй, я побігла міняти крапельниці.'
          }
        }
      ],
      prompt: { en: 'What do you write to Dr. Kovalenko?', ua: 'Що пишеш лікарю Коваленку?' },
      options: [
        {
          text: {
            en: 'Potassium 2.9 noted. The order has no diluent or rate, and concentrated potassium is never pushed. Confirm: 20 ml in 500 ml saline over 4 hours? I hold it until you reply.',
            ua: 'Калій 2,9 бачу. У призначенні немає розчинника й швидкості, а концентрований калій струминно не вводять. Підтвердьте: 20 мл на 500 мл фізрозчину за 4 години? Тримаю, поки не відповісте.'
          },
          q: 'best',
          fx: { safety: 6, trust: 3 },
          fb: {
            en: 'You named the gap, proposed the fix and set a clear hold. The doctor types one word and the patient gets a safe infusion within minutes.',
            ua: 'Ти назвала прогалину, запропонувала рішення й чітко сказала, що чекаєш. Лікар друкує одне слово, і пацієнтка за кілька хвилин отримує безпечну інфузію.'
          }
        },
        {
          text: {
            en: 'Will do. I am diluting it in 500 ml saline over 4 hours as the ward protocol says, since the order has no rate. Please add that to the chart when you are back on the ward.',
            ua: 'Добре. Розводжу на 500 мл фізрозчину на 4 години за протоколом відділення, бо в призначенні немає швидкості. Допишіть це в лист, коли повернетесь у відділення, будь ласка.'
          },
          q: 'ok',
          fx: { safety: 1, flow: 2 },
          fb: {
            en: 'The patient is safe and the protocol backs you. But you gave a drug on an order you completed yourself; if the doctor meant something else, nobody confirmed it.',
            ua: 'Пацієнтка в безпеці, і протокол на твоєму боці. Але ти ввела препарат за призначенням, яке дописала сама; якщо лікар мав на увазі інше, ніхто цього не підтвердив.'
          }
        },
        {
          text: {
            en: 'Giving it now as written. You are the doctor and her potassium is low, so I will not hold up treatment while you are busy with the emergency room, we can tidy the chart later.',
            ua: 'Вводжу зараз, як написано. Ви лікар, калій низький, тож не затримуватиму лікування, поки ви зайняті в приймальному, а лист доведемо до ладу потім, коли буде час.'
          },
          q: 'bad',
          fx: { safety: -15, trust: -5 },
          fb: {
            en: 'Undiluted potassium given as a push can stop the heart. "The doctor wrote it" protects neither the patient nor you; the nurse who administers is responsible for checking.',
            ua: 'Нерозведений калій струминно може зупинити серце. «Лікар так написав» не захищає ні пацієнтку, ні тебе: медсестра, яка вводить, відповідає за перевірку.'
          },
          later: {
            after: 2,
            note: {
              en: 'Nadiia saw the potassium entry and asked who checked the dilution. The conversation was not pleasant.',
              ua: 'Надія побачила запис про калій і спитала, хто перевіряв розведення. Розмова була неприємна.'
            },
            fx: { team: -4 }
          }
        },
        {
          text: {
            en: 'The order is incomplete, so I am not giving it. We will sort it out when you are back on the ward around 10:00; this is not something to decide by message while you are in the ER.',
            ua: 'Призначення неповне, тому не вводжу. Розберемось, коли повернетесь у відділення близько 10:00; це не те, що варто вирішувати повідомленнями, поки ви у приймальному.'
          },
          q: 'bad',
          fx: { safety: -5, flow: -4 },
          fb: {
            en: 'Potassium 2.9 can cause arrhythmia and you parked it for 80 minutes. Refusing was right; refusing without offering a way forward is not.',
            ua: 'Калій 2,9 може дати аритмію, а ти відклала це на 80 хвилин. Відмовитися було правильно; відмовитися, не запропонувавши виходу, — ні.'
          }
        }
      ],
      principle: {
        en: 'Unclear order: hold, name the gap, propose the fix.',
        ua: 'Незрозуміле призначення: тримай, назви прогалину, запропонуй рішення.'
      }
    },
    {
      type: 'numeric',
      id: 's3',
      at: '09:20',
      comp: ['accuracy'],
      traits: ['C', 'I'],
      text: {
        en: 'Mrs. Lysenko in bed 11 is charted for intravenous paracetamol at 15 mg per kg of body weight. The pharmacy supplies it as a 10 mg/ml solution.',
        ua: 'Пані Лисенко з ліжка 11 призначено парацетамол внутрішньовенно з розрахунку 15 мг на кг маси тіла. Аптека видає розчин 10 мг/мл.'
      },
      data: [
        { label: { en: 'Body weight', ua: 'Маса тіла' }, value: { en: '56 kg', ua: '56 кг' } },
        { label: { en: 'Dose', ua: 'Доза' }, value: { en: '15 mg/kg', ua: '15 мг/кг' } },
        { label: { en: 'Solution', ua: 'Розчин' }, value: { en: '10 mg/ml', ua: '10 мг/мл' } }
      ],
      question: { en: 'How many ml do you draw up?', ua: 'Скільки мл набираєш?' },
      answer: 84,
      tolerance: 0.02,
      unit: { en: 'ml', ua: 'мл' },
      hint: {
        en: 'First the total dose in mg, then divide by the concentration.',
        ua: 'Спершу загальна доза в мг, потім поділи на концентрацію.'
      },
      explain: {
        en: '15 mg × 56 kg = 840 mg. 840 mg ÷ 10 mg/ml = 84 ml. That is below the 1 g single-dose ceiling, so the order is safe to give.',
        ua: '15 мг × 56 кг = 840 мг. 840 мг ÷ 10 мг/мл = 84 мл. Це менше за максимальну разову дозу 1 г, тож призначення безпечне.'
      },
      fx: { ok: { safety: 5 }, fail: { safety: -6 } }
    },
    {
      type: 'queue',
      id: 's4',
      at: '10:15',
      comp: ['priorities', 'clinical'],
      traits: ['S', 'R'],
      text: {
        en: 'Four call buttons light up within a minute. Open them in the order you think is right and answer each.',
        ua: 'За хвилину загоряються чотири кнопки виклику. Відкрий їх у тому порядку, який вважаєш правильним, і відповідай на кожну.'
      },
      tickets: [
        {
          id: 't1',
          from: 'aide',
          urgency: 3,
          sla: { en: 'now', ua: 'зараз' },
          subject: { en: 'Bed 2 is on the floor', ua: 'Ліжко 2, пацієнтка на підлозі' },
          body: {
            en: 'Mrs. Hnatiuk climbed over the rail. She is on the floor beside the bed, awake, holding her hip. I have not moved her.',
            ua: 'Пані Гнатюк перелізла через бортик. Лежить на підлозі біля ліжка, притомна, тримається за стегно. Я її не рухав.'
          },
          options: [
            {
              text: {
                en: 'Good that you did not move her. I am coming now; check she is breathing and talking, keep her warm. I assess the hip before we lift and I call the doctor.',
                ua: 'Добре, що не рухав. Іду зараз; перевір, чи дихає й говорить, укрий її. Огляну стегно, перш ніж піднімати, і покличу лікаря.'
              },
              q: 'best',
              fx: { safety: 6, team: 2 },
              fb: {
                en: 'A hip held after a fall is a fracture until proven otherwise. Assessing before lifting and calling the doctor protect her; the incident report later protects the ward.',
                ua: 'Стегно, за яке тримаються після падіння, — це перелом, поки не доведено інше. Огляд перед підйомом і виклик лікаря захищають її; протокол інциденту потім захистить відділення.'
              }
            },
            {
              text: {
                en: 'Help her sit up slowly and get her back to bed with me, then we check her vitals and I tell the doctor when he comes on rounds.',
                ua: 'Допоможи їй повільно сісти, разом покладемо в ліжко, потім перевіримо показники, а лікарю скажу, коли прийде на обхід.'
              },
              q: 'ok',
              fx: { safety: -3 },
              fb: {
                en: 'Fast and kind, but you lifted a possible hip fracture before anyone looked at it. The doctor should hear about a fall now, not on rounds.',
                ua: 'Швидко і по-людськи, але ви підняли можливий перелом стегна, перш ніж хтось його оглянув. Про падіння лікар має дізнатися зараз, а не на обході.'
              }
            },
            {
              text: {
                en: 'Get her back into bed and put the rail up properly this time. I am in the middle of a drip and will check on her in ten minutes.',
                ua: 'Поклади її назад у ліжко й цього разу підніми бортик як слід. Я на крапельниці, зайду до неї за десять хвилин.'
              },
              q: 'bad',
              fx: { safety: -10, team: -4 },
              fb: {
                en: 'An 80-year-old on the floor holding her hip is the most urgent thing on the ward, and you delegated it to an aide. Ten minutes is how an unseen fracture becomes a complication.',
                ua: '80-річна жінка на підлозі, що тримається за стегно, — найтерміновіше у відділенні, а ти передала це молодшому медбрату. Десять хвилин — це час, за який непомічений перелом стає ускладненням.'
              }
            }
          ]
        },
        {
          id: 't2',
          from: { en: 'Bed 11, Mrs. Lysenko', ua: 'Ліжко 11, пані Лисенко' },
          urgency: 2,
          sla: { en: '10 min', ua: '10 хв' },
          subject: { en: 'The drip arm is swelling', ua: 'Рука під крапельницею набрякає' },
          body: {
            en: 'The antibiotic is running and my hand is burning. It looks puffy around the needle and the drip is going slower than before.',
            ua: 'Крапає антибіотик, а кисть пече. Навколо голки ніби припухло, і крапає повільніше, ніж раніше.'
          },
          options: [
            {
              text: {
                en: 'Stop the infusion now, take the cannula out, raise the arm and put a cool compress on it. Re-site in the other arm and note the infiltration in the chart.',
                ua: 'Зупинити інфузію, вийняти канюлю, підняти руку й покласти прохолодний компрес. Поставити нову канюлю в іншу руку й записати інфільтрацію в лист.'
              },
              q: 'best',
              fx: { safety: 4 },
              fb: {
                en: 'Burning, swelling and a slowing drip are the three signs of infiltration. Every minute it runs, more antibiotic goes into tissue instead of a vein.',
                ua: 'Печіння, набряк і сповільнення крапель — три ознаки інфільтрації. Кожна хвилина — це ще антибіотик у тканину замість вени.'
              }
            },
            {
              text: {
                en: 'Slow the drip right down, tell her to keep the arm still, and check the site again in 15 minutes before deciding whether to re-site the cannula.',
                ua: 'Сильно сповільнити крапельницю, попросити не рухати рукою й за 15 хвилин знову глянути на місце, перш ніж вирішувати, чи переставляти канюлю.'
              },
              q: 'ok',
              fx: { safety: -2 },
              fb: {
                en: 'Slowing does not fix a cannula that is out of the vein. You bought 15 minutes of more swelling for a decision you could make now.',
                ua: 'Сповільнення не рятує канюлю, яка вийшла з вени. Ти виграла 15 хвилин додаткового набряку для рішення, яке можна ухвалити зараз.'
              }
            },
            {
              text: {
                en: 'Reassure her that this antibiotic often stings and that the arm will settle once the bag finishes in about half an hour; no need to disturb the line.',
                ua: 'Заспокоїти її: цей антибіотик часто пече, рука заспокоїться, щойно за пів години докрапає; чіпати систему не варто.'
              },
              q: 'bad',
              fx: { safety: -8 },
              fb: {
                en: 'Some drugs sting in a vein, but swelling means the vein is no longer where the drug is going. Half an hour of that can mean tissue damage.',
                ua: 'Деякі препарати печуть і у вені, але набряк означає, що препарат уже не у вені. Пів години такого — це можливе ушкодження тканин.'
              }
            }
          ]
        },
        {
          id: 't3',
          from: { en: 'Bed 15, Mr. Savchuk', ua: 'Ліжко 15, пан Савчук' },
          urgency: 2,
          sla: { en: '20 min', ua: '20 хв' },
          subject: { en: 'My back is killing me', ua: 'Спина просто розривається' },
          body: {
            en: 'Nobody gave me a painkiller this morning. I had one at night and it helped. Can you bring one, it is a 7 out of 10 now.',
            ua: 'Зранку ніхто не дав знеболювального. Вночі давали, допомогло. Принесіть, будь ласка, зараз болить на 7 із 10.'
          },
          options: [
            {
              text: {
                en: 'Check his chart: the as-needed painkiller was given at 05:30 and is allowed every 6 hours, so he is due. Give it and record the pain score before and after.',
                ua: 'Перевірити лист: знеболювальне «за потреби» давали о 05:30, дозволено кожні 6 годин, тож уже можна. Дати й записати оцінку болю до і після.'
              },
              q: 'best',
              fx: { safety: 3, flow: 3 },
              fb: {
                en: 'The chart tells you the dose is due and the pain score makes the next decision easier. Pain treated on time costs less than pain that has escalated.',
                ua: 'Лист показує, що доза вже дозволена, а оцінка болю полегшує наступне рішення. Біль, знятий вчасно, коштує менше, ніж біль, що розрісся.'
              }
            },
            {
              text: {
                en: 'Tell him the doctor will be on the ward by 11:00 and can review his pain relief then, and offer to reposition him with an extra pillow in the meantime.',
                ua: 'Сказати, що лікар буде у відділенні до 11:00 і перегляне знеболення, а поки запропонувати зручніше вкластися з додатковою подушкою.'
              },
              q: 'ok',
              fx: { flow: -2, trust: -2 },
              fb: {
                en: 'Repositioning helps, but a prescribed as-needed dose does not need a doctor. He waits 45 minutes in pain for a decision already in the chart.',
                ua: 'Зручне положення допомагає, але призначена доза «за потреби» лікаря не потребує. Він 45 хвилин терпить біль через рішення, яке вже записане в листі.'
              }
            },
            {
              text: {
                en: 'Bring him a tablet from the ward stock, it is only a mild painkiller, and write it up later when the round of drips is done and there is a quiet minute.',
                ua: 'Принести таблетку з запасу відділення, це ж звичайне знеболювальне, а записати потім, коли закінчиш із крапельницями й буде спокійна хвилина.'
              },
              q: 'bad',
              fx: { safety: -6 },
              fb: {
                en: 'Nothing is "only" a tablet when it is not charted at the time. The night dose, the daily maximum and the next shift all live in that chart.',
                ua: 'Жодна таблетка не «звичайна», якщо її не записано одразу. Нічна доза, добовий максимум і наступна зміна — усе живе в цьому листі.'
              }
            }
          ]
        },
        {
          id: 't4',
          from: { en: 'Bed 5, Mr. Oliinyk', ua: 'Ліжко 5, пан Олійник' },
          urgency: 1,
          sla: { en: 'when you can', ua: 'коли зможеш' },
          subject: { en: 'TV and window', ua: 'Телевізор і вікно' },
          body: {
            en: 'The remote is dead and the window next to me is open, it is getting cold. Not urgent, but someone promised yesterday.',
            ua: 'Пульт не працює, а вікно біля мене відчинене, стає холодно. Не терміново, але вчора хтось обіцяв.'
          },
          options: [
            {
              text: {
                en: "Reply that Yurii will close the window within 10 minutes and bring batteries after lunch. Put it on the aide's list so it does not get lost again.",
                ua: 'Відповісти, що Юрій за 10 хвилин зачинить вікно, а батарейки принесе після обіду. Записати в список для молодшого медбрата, щоб не загубилося знову.'
              },
              q: 'best',
              fx: { flow: 2, trust: 2 },
              fb: {
                en: 'Low urgency gets a time and a name, not silence. A cold patient becomes a complaint; a promise kept the second time rebuilds trust.',
                ua: 'Низька терміновість отримує час і ім’я, а не тишу. Пацієнт у холоді стає скаргою; обіцянка, дотримана з другого разу, повертає довіру.'
              }
            },
            {
              text: {
                en: 'Go and close the window yourself right now on the way to bed 11; the batteries can wait until the afternoon round when you pass his bed anyway.',
                ua: 'Самій зачинити вікно просто зараз, по дорозі до ліжка 11; батарейки почекають до обіднього обходу, коли й так проходитимеш повз його ліжко.'
              },
              q: 'ok',
              fx: { energy: -3 },
              fb: {
                en: 'Done is done, but you spent nurse time on a task the aide exists for. Multiply by ten requests a shift and your drips run late.',
                ua: 'Зроблено так зроблено, але ти витратила час медсестри на те, для чого є молодший медбрат. Помнож на десять таких за зміну, і крапельниці запізнюються.'
              }
            },
            {
              text: {
                en: 'Leave it unanswered for now; it is not a medical request, and he can ask Yurii himself the next time he walks past the bay.',
                ua: 'Поки не відповідати; це не медичне питання, і він сам може попросити Юрія, коли той наступного разу проходитиме палатою.'
              },
              q: 'bad',
              fx: { trust: -4 },
              fb: {
                en: 'A call button nobody answers teaches patients that buttons do not work. The next time he does not press it may be the time it matters.',
                ua: 'Кнопка, на яку ніхто не відповідає, вчить пацієнтів, що кнопки не працюють. Наступного разу він може не натиснути, коли це буде важливо.'
              }
            }
          ]
        }
      ]
    },
    {
      type: 'choice',
      id: 's5',
      at: '11:30',
      comp: ['ethics', 'communication'],
      traits: ['S', 'C'],
      text: {
        en: 'The ward phone rings. "This is Oksana, Mr. Bondar\'s daughter from bed 4. I know he had tests this morning. What did they show, is it his heart? Please, I am his family, just tell me."',
        ua: 'Дзвонить телефон відділення. «Це Оксана, донька пана Бондаря з четвертого ліжка. Знаю, що зранку йому робили аналізи. Що показали, це серце? Я ж рідна донька, просто скажіть».'
      },
      options: [
        {
          text: {
            en: 'Say you understand her worry, but results are not given by phone and only to people the patient has named. Offer to ask her father today and to arrange a call with Dr. Kovalenko.',
            ua: 'Сказати, що розумієш тривогу, але результати по телефону не дають, і лише тим, кого назвав пацієнт. Запропонувати сьогодні спитати батька й домовитись про розмову з лікарем.'
          },
          q: 'best',
          fx: { safety: 3, trust: 3 },
          fb: {
            en: 'You cannot verify a voice on the phone, and the diagnosis belongs to the patient first. She hears a path to the answer today, not a closed door.',
            ua: 'Голос у слухавці не перевіриш, а діагноз належить насамперед пацієнту. Вона чує шлях до відповіді вже сьогодні, а не зачинені двері.'
          }
        },
        {
          text: {
            en: 'Tell her that he is stable and comfortable, that the doctor has seen him this morning, and that anything about the results she needs to hear from the doctor in person.',
            ua: 'Сказати, що він стабільний, йому зручно, лікар оглянув його зранку, а все, що стосується результатів, їй варто почути від лікаря особисто, у відділенні.'
          },
          q: 'ok',
          fx: { trust: 1 },
          fb: {
            en: '"Stable" is already clinical information to someone you have not identified. Mostly fine, but you gave her no time or step to get the real answer.',
            ua: '«Стабільний» — це вже клінічна інформація людині, яку ти не ідентифікувала. Загалом нормально, але ти не дала їй ні часу, ні кроку до справжньої відповіді.'
          }
        },
        {
          text: {
            en: 'She is clearly his daughter and worried sick, so tell her the ECG and troponin point to a heart problem and that the cardiologist is coming this afternoon.',
            ua: 'Вона явно донька й страшенно хвилюється, тож сказати, що ЕКГ і тропонін вказують на проблему із серцем і що кардіолог прийде сьогодні по обіді.'
          },
          q: 'bad',
          fx: { safety: -8, trust: -4 },
          fb: {
            en: 'You disclosed a diagnosis to an unverified caller before the patient heard it from his doctor. Kindness that breaks confidentiality is still a breach.',
            ua: 'Ти розкрила діагноз неперевіреній людині раніше, ніж пацієнт почув його від лікаря. Доброта, яка порушує конфіденційність, усе одно порушення.'
          }
        },
        {
          text: {
            en: 'Tell her the ward does not give out any information by phone, full stop, and that she should come in during visiting hours like everyone else.',
            ua: 'Сказати, що відділення не дає жодної інформації по телефону, крапка, і хай приходить у години відвідування, як усі інші родичі пацієнтів.'
          },
          q: 'bad',
          fx: { trust: -5 },
          fb: {
            en: 'Right rule, wrong delivery. She hangs up feeling shut out, calls the hotline, and someone spends an hour on what one warm sentence would have solved.',
            ua: 'Правило правильне, подача ні. Вона кладе слухавку з відчуттям, що її відштовхнули, дзвонить на гарячу лінію, і хтось витрачає годину на те, що вирішило б одне тепле речення.'
          }
        }
      ],
      principle: {
        en: "Confidentiality is the patient's. Your job is to open a door, not slam one.",
        ua: 'Конфіденційність належить пацієнту. Твоя робота — відчинити двері, а не грюкнути ними.'
      }
    },
    {
      type: 'spot',
      id: 's6',
      at: '12:40',
      comp: ['accuracy', 'clinical'],
      traits: ['C', 'I'],
      text: {
        en: 'Svitlana asks you to double-check the 14:00 medication round she prepared from the chart. The chart has 3 mistakes. Tap each one.',
        ua: 'Світлана просить перевірити роздачу ліків на 14:00, яку вона підготувала за листом призначень. У листі 3 помилки. Натисни на кожну.'
      },
      mock: {
        kind: 'table',
        title: { en: 'Medication chart, 14:00 round', ua: 'Лист призначень, роздача о 14:00' },
        blocks: [
          {
            id: 'b1',
            kind: 'heading',
            label: {
              en: 'Internal medicine, bay 3, 7 patients',
              ua: 'Терапія, палати 3-го поста, 7 пацієнтів'
            },
            value: ''
          },
          {
            id: 'b2',
            kind: 'note',
            label: { en: 'Allergies (from admission notes)', ua: 'Алергії (з історій хвороби)' },
            value: {
              en: 'Bed 9 Shevchuk: penicillins. Bed 12 Marchenko: none known.',
              ua: 'Ліжко 9 Шевчук: пеніциліни. Ліжко 12 Марченко: не відомі.'
            }
          },
          {
            id: 'b3',
            kind: 'line',
            label: { en: 'Bed 4 · Bondar', ua: 'Ліжко 4 · Бондар' },
            value: {
              en: 'Acetylsalicylic acid 100 mg · oral · 14:00',
              ua: 'Ацетилсаліцилова кислота 100 мг · перорально · 14:00'
            }
          },
          {
            id: 'b4',
            kind: 'line',
            label: { en: 'Bed 7 · Tkachuk', ua: 'Ліжко 7 · Ткачук' },
            value: {
              en: 'Metformin 1000 mg · oral · with lunch',
              ua: 'Метформін 1000 мг · перорально · з обідом'
            }
          },
          {
            id: 'b5',
            kind: 'line',
            label: { en: 'Bed 9 · Shevchuk', ua: 'Ліжко 9 · Шевчук' },
            value: {
              en: 'Amoxicillin 500 mg · oral · 14:00',
              ua: 'Амоксицилін 500 мг · перорально · 14:00'
            },
            issue: {
              en: 'Amoxicillin is a penicillin, and bed 9 has a penicillin allergy recorded three rows up.',
              ua: 'Амоксицилін — пеніцилін, а в ліжка 9 трьома рядками вище записана алергія на пеніциліни.'
            }
          },
          {
            id: 'b6',
            kind: 'line',
            label: { en: 'Bed 11 · Lysenko', ua: 'Ліжко 11 · Лисенко' },
            value: {
              en: 'Paracetamol 1000 mg · IV · 12:00',
              ua: 'Парацетамол 1000 мг · в/в · 12:00'
            }
          },
          {
            id: 'b7',
            kind: 'line',
            label: { en: 'Bed 11 · Lysenko', ua: 'Ліжко 11 · Лисенко' },
            value: {
              en: 'Paracetamol 1000 mg · IV · 14:00',
              ua: 'Парацетамол 1000 мг · в/в · 14:00'
            },
            issue: {
              en: 'A second gram of paracetamol two hours after the first. The minimum interval is 4 hours and the daily cap is 4 g.',
              ua: 'Другий грам парацетамолу через дві години після першого. Мінімальний інтервал 4 години, добовий максимум 4 г.'
            }
          },
          {
            id: 'b8',
            kind: 'line',
            label: { en: 'Bed 12 · Marchenko', ua: 'Ліжко 12 · Марченко' },
            value: {
              en: 'Digoxin 2.5 mg · oral · 14:00',
              ua: 'Дигоксин 2,5 мг · перорально · 14:00'
            },
            issue: {
              en: 'Digoxin maintenance doses are 0.125–0.25 mg. 2.5 mg is ten times too much, a classic decimal-point error.',
              ua: 'Підтримувальна доза дигоксину — 0,125–0,25 мг. 2,5 мг — удесятеро більше, класична помилка з комою.'
            }
          },
          {
            id: 'b9',
            kind: 'line',
            label: { en: 'Bed 15 · Savchuk', ua: 'Ліжко 15 · Савчук' },
            value: {
              en: 'Omeprazole 20 mg · oral · 14:00',
              ua: 'Омепразол 20 мг · перорально · 14:00'
            }
          },
          {
            id: 'b10',
            kind: 'button',
            label: { en: 'Confirm round', ua: 'Підтвердити роздачу' },
            value: ''
          }
        ]
      },
      explain: {
        en: 'Three errors in seven rows: a penicillin for a patient with a penicillin allergy, a second gram of paracetamol two hours after the first, and digoxin at ten times a normal dose. The allergy note was on the same sheet; a chart is read whole, not row by row.',
        ua: 'Три помилки на сім рядків: пеніцилін пацієнтці з алергією на пеніциліни, другий грам парацетамолу через дві години після першого й дигоксин у десятикратній дозі. Запис про алергію був на тому самому аркуші; лист читають цілком, а не рядок за рядком.'
      },
      fx: { ok: { safety: 8 }, fail: { safety: -8 } }
    },
    {
      type: 'chat',
      id: 's7',
      at: '14:00',
      comp: ['ethics', 'communication'],
      traits: ['S', 'C'],
      messages: [
        {
          from: 'nurse',
          text: {
            en: 'Bed 11 done, re-siting bed 2 now, then six drips to change before 15:00. Do not wait for me with the chart check.',
            ua: 'Ліжко 11 готове, зараз переставляю канюлю на ліжку 2, потім шість крапельниць до 15:00. Не чекай мене з перевіркою листа.'
          }
        },
        {
          from: 'aide',
          text: {
            en: '(quietly) She went straight from bed 11 to bed 2 in the same gloves. Did not touch the sanitiser. Should I say something?',
            ua: '(тихо) Вона пішла від ліжка 11 до ліжка 2 в тих самих рукавичках. До антисептика не торкалась. Мені щось сказати?'
          }
        }
      ],
      prompt: { en: 'What do you do?', ua: 'Що робиш?' },
      options: [
        {
          text: {
            en: 'Walk over, hand her the sanitiser and fresh gloves with a quiet "hands first, I will take two of your drips", and then actually take them.',
            ua: 'Підійти, подати їй антисептик і нові рукавички з тихим «спочатку руки, я заберу дві твої крапельниці», і справді забрати їх.'
          },
          q: 'best',
          fx: { safety: 6, team: 3 },
          fb: {
            en: 'Bed 2 is protected right now, Svitlana keeps her face in front of the patient, and the real cause, overload, gets a fix instead of a lecture.',
            ua: 'Ліжко 2 захищене просто зараз, Світлана не втрачає обличчя перед пацієнткою, а справжня причина, перевантаження, отримує рішення замість нотації.'
          }
        },
        {
          text: {
            en: 'Say nothing now, but report the missed hand hygiene to Nadiia before the end of the shift so that it is on record and gets dealt with properly.',
            ua: 'Зараз промовчати, але до кінця зміни повідомити Надію про пропущену гігієну рук, щоб це було зафіксовано й розібрано як належить.'
          },
          q: 'ok',
          fx: { safety: -2, team: -5 },
          fb: {
            en: 'The record is correct and the patient was still exposed. Going over her head before talking to her is how a colleague becomes an opponent.',
            ua: 'Запис правильний, а пацієнтка все одно під ризиком. Іти через голову, не поговоривши з колегою, — так із неї роблять опонентку.'
          }
        },
        {
          text: {
            en: 'Let it go. She has twenty years on this ward and six drips waiting, and the gloves looked clean enough from where you stand.',
            ua: 'Облишити. У неї двадцять років у цьому відділенні й шість крапельниць чекають, а рукавички звідти, де ти стоїш, виглядали чистими.'
          },
          q: 'bad',
          fx: { safety: -10 },
          fb: {
            en: 'Bed 11 has pneumonia and bed 2 is about to get a new line into her vein. Gloves that "look clean" are why ward infections exist.',
            ua: 'На ліжку 11 пневмонія, а ліжку 2 зараз ставлять нову канюлю у вену. Рукавички, які «виглядають чистими», — саме тому й існують внутрішньолікарняні інфекції.'
          },
          later: {
            after: 2,
            note: {
              en: 'Mrs. Hnatiuk in bed 2 has a red, warm cannula site. Dr. Kovalenko asks who placed it and how.',
              ua: 'У пані Гнатюк на ліжку 2 місце канюлі почервоніло й гаряче. Лікар Коваленко питає, хто ставив і як.'
            },
            fx: { safety: -5 }
          }
        },
        {
          text: {
            en: 'Call across the bay: "Svitlana, hands! You cannot go from bed 11 to bed 2 like that." Everyone should hear it so that it does not happen again.',
            ua: 'Гукнути через палату: «Світлано, руки! Так від ліжка 11 до ліжка 2 ходити не можна». Нехай усі почують, щоб більше не повторювалось.'
          },
          q: 'bad',
          fx: { safety: 3, team: -8 },
          fb: {
            en: 'The patient is protected, but Svitlana was corrected in front of six beds. She will remember the humiliation longer than the lesson, and so will the patients.',
            ua: 'Пацієнтка захищена, але Світлану виправили перед шістьма ліжками. Приниження вона пам’ятатиме довше за урок, і пацієнти теж.'
          }
        }
      ],
      principle: {
        en: "Stop the risk now, protect the colleague's face, fix the cause.",
        ua: 'Зупини ризик зараз, збережи обличчя колеги, виправ причину.'
      }
    },
    {
      type: 'choice',
      id: 's8',
      at: '15:30',
      comp: ['ethics', 'clinical'],
      traits: ['S', 'E'],
      text: {
        en: 'Mr. Marchenko in bed 12, admitted yesterday with pancreatitis, is shouting in the corridor. He wants his things and a discharge paper "now". He has shoved Yurii against the wall and is red in the face. Two patients watch from their doors.',
        ua: 'Пан Марченко з ліжка 12, учора госпіталізований із панкреатитом, кричить у коридорі. Вимагає свої речі й виписку «негайно». Він штовхнув Юрія на стіну, обличчя червоне. Двоє пацієнтів дивляться з дверей.'
      },
      options: [
        {
          text: {
            en: 'Keep two metres away, ask Yurii to call Dr. Kovalenko and security, and speak low and slow: "I hear you want to leave. Let us sit down and I will have the doctor here in five minutes."',
            ua: 'Тримати дистанцію два метри, попросити Юрія викликати лікаря Коваленка й охорону, говорити тихо й повільно: «Чую, що ви хочете додому. Сядьмо, і за п’ять хвилин я приведу лікаря».'
          },
          q: 'best',
          fx: { safety: 6, team: 3 },
          fb: {
            en: 'Distance and help on the way keep everyone safe; a calm voice that names what he wants lowers the temperature. Nobody has to win this argument.',
            ua: 'Дистанція й підмога в дорозі бережуть усіх; спокійний голос, який називає його бажання, знижує градус. Цю суперечку нікому не треба вигравати.'
          }
        },
        {
          text: {
            en: 'Go to him alone and talk him down; you know him from the morning round and he has calmed down before. Call the doctor once he is back in bed and the corridor is quiet.',
            ua: 'Підійти самій і заспокоїти; ти знаєш його з ранкового обходу, він уже заспокоювався. Лікаря покликати, коли він повернеться в ліжко й у коридорі стане тихо.'
          },
          q: 'ok',
          fx: { safety: -3, trust: 2 },
          fb: {
            en: 'It may work, and it may not: he has already pushed a colleague. De-escalation is right; doing it alone, with no one called, is the part that goes wrong.',
            ua: 'Може спрацювати, а може й ні: він уже штовхнув колегу. Деескалація правильна; робити це самій, нікого не покликавши, і є те, що зазвичай іде не так.'
          }
        },
        {
          text: {
            en: "Tell him firmly that if he does not go back to his bed this minute you will call the police, and that leaving against medical advice is his problem, not the ward's.",
            ua: 'Твердо сказати, що якщо він цієї ж хвилини не повернеться в ліжко, ти викличеш поліцію, а виписка всупереч лікарям — його проблема, а не відділення.'
          },
          q: 'bad',
          fx: { safety: -8, trust: -4 },
          fb: {
            en: 'A threat to a frightened, angry man is a dare. The corridor now has a fight, two witnesses and a complaint, and his pancreatitis is still untreated.',
            ua: 'Погроза наляканому розлюченому чоловіку — це виклик. У коридорі тепер бійка, двоє свідків і скарга, а панкреатит так і не лікують.'
          }
        },
        {
          text: {
            en: 'Fetch his bag and the refusal-of-treatment form; he is an adult, and the sooner he signs and leaves, the sooner the ward is quiet again and you can get back to the drips.',
            ua: 'Принести його сумку й форму відмови від лікування; він дорослий, і що швидше підпише й піде, то швидше у відділенні знову стане тихо, а ти повернешся до крапельниць.'
          },
          q: 'bad',
          fx: { safety: -10 },
          fb: {
            en: "An agitated patient with acute pancreatitis is not in a state to refuse treatment, and assessing that is the doctor's job. You chose quiet over safety.",
            ua: 'Збуджений пацієнт із гострим панкреатитом не в стані відмовлятися від лікування, і оцінювати це має лікар. Ти обрала тишу замість безпеки.'
          }
        }
      ],
      principle: {
        en: 'Distance, help, low voice. Never alone, never a dare.',
        ua: 'Дистанція, підмога, тихий голос. Ніколи сама, ніколи на слабо.'
      }
    },
    {
      type: 'numeric',
      id: 's9',
      at: '16:40',
      comp: ['accuracy'],
      traits: ['C', 'R'],
      text: {
        en: 'The doctor orders 500 ml of saline for Mr. Bondar over 4 hours. The giving set on the ward delivers 20 drops per ml.',
        ua: 'Лікар призначає пану Бондарю 500 мл фізрозчину за 4 години. Система у відділенні дає 20 крапель на мл.'
      },
      data: [
        { label: { en: 'Volume', ua: 'Об’єм' }, value: { en: '500 ml', ua: '500 мл' } },
        { label: { en: 'Time', ua: 'Час' }, value: { en: '4 h', ua: '4 год' } },
        {
          label: { en: 'Giving set', ua: 'Система' },
          value: { en: '20 drops/ml', ua: '20 крап./мл' }
        }
      ],
      question: {
        en: 'At how many drops per minute do you set the drip?',
        ua: 'Скільки крапель за хвилину виставляєш?'
      },
      answer: 41.7,
      tolerance: 0.05,
      unit: { en: 'drops/min', ua: 'крап./хв' },
      hint: {
        en: 'Total drops first, then divide by the minutes.',
        ua: 'Спершу загальна кількість крапель, потім поділи на хвилини.'
      },
      explain: {
        en: '500 ml × 20 drops = 10 000 drops. 4 hours = 240 minutes. 10 000 ÷ 240 ≈ 41.7, so you count about 42 drops a minute.',
        ua: '500 мл × 20 крапель = 10 000 крапель. 4 години = 240 хвилин. 10 000 ÷ 240 ≈ 41,7, тож рахуєш близько 42 крапель за хвилину.'
      },
      fx: { ok: { flow: 4, safety: 2 }, fail: { safety: -4 } }
    }
  ],
  verdict: {
    bands: [
      {
        min: 0.8,
        title: {
          en: 'The nurse others ask to double-check',
          ua: 'Медсестра, яку просять перевірити'
        },
        text: {
          en: 'You held unclear orders, ranked by physiology instead of noise, and protected colleagues without lecturing them. That is what a head nurse looks for.',
          ua: 'Ти тримала незрозумілі призначення, ранжувала за фізіологією, а не за гучністю, і захищала колег без нотацій. Саме це шукає старша медсестра.'
        }
      },
      {
        min: 0.6,
        title: {
          en: 'Safe hands, a few slow moments',
          ua: 'Надійні руки, кілька повільних моментів'
        },
        text: {
          en: 'Most calls were right; the misses were about speed, waiting for a doctor or a round when the chart or the protocol already had the answer.',
          ua: 'Більшість рішень правильні; промахи були про швидкість: ти чекала лікаря або обходу, коли відповідь уже була в листі чи протоколі.'
        }
      },
      {
        min: 0.4,
        title: {
          en: 'You would get through the shift, not calmly',
          ua: 'Зміну ти б витримала, але не спокійно'
        },
        text: {
          en: 'You tend to trust the louder request or the senior voice over the numbers. Re-read the feedback on the potassium order and the fall.',
          ua: 'Ти схильна довіряти гучнішому запиту або старшому голосу більше, ніж цифрам. Перечитай фідбек про калій і про падіння.'
        }
      },
      {
        min: 0,
        title: {
          en: 'This ward would have been dangerous today',
          ua: 'У цьому відділенні сьогодні було б небезпечно'
        },
        text: {
          en: 'Several choices put speed or kindness ahead of checking. In nursing, the check is the kindness.',
          ua: 'Кілька рішень ставили швидкість чи доброту вище за перевірку. У медсестринстві перевірка і є добротою.'
        }
      }
    ],
    reality: [
      {
        en: 'Roughly a third of a shift is charts and records: every drug, every drip, every fall written down twice.',
        ua: 'Приблизно третина зміни — листи й журнали: кожен препарат, крапельниця й падіння записані двічі.'
      },
      {
        en: 'You walk 8 to 10 km on a 12-hour shift and sit down for maybe forty minutes of it.',
        ua: 'За 12-годинну зміну ти проходиш 8–10 км і сидиш із них хіба хвилин сорок.'
      },
      {
        en: 'Two or three times a week someone shouts at you for something you did not decide: a doctor, a relative, a patient in pain.',
        ua: 'Два-три рази на тиждень на тебе кричать за те, чого ти не вирішувала: лікар, родич, пацієнт, якому боляче.'
      }
    ]
  }
};
